import { useCallback, useEffect, useState } from 'react';
import { Card, Dropdown, Spinner } from 'react-bootstrap';
import Scrollbar from 'components/base/Scrollbar';
import ActivityLogItem from 'components/common/ActivityLogItem';
import classNames from 'classnames';
import { useInfiniteQuery, useQuery } from 'react-query';
import {
  getAdminNotifications,
  AdminActivityEntityType,
  AdminActivityResponse
} from 'services/api/notification';
import LoadingPlaceholder from 'components/loading-placeholder/LoadingPlaceholder';
import { useAccessToken } from 'providers/AccessTokenProvider';
import { GET_EMPLOYEES_QUERY } from 'services/api/employee';
import { Employee } from 'data/users';

const NOTIFICATIONS_PER_PAGE = 10;

const ActivityDropdownMenu = ({ className }: { className?: string }) => {
  const { authUser } = useAccessToken();
  const [isMenuMounted, setIsMenuMounted] = useState<boolean>(false);
  const [activityType, setActivityType] = useState<
    AdminActivityEntityType | 'all'
  >('all');
  const [filterBy, setFilterBy] = useState<'none' | 'username' | 'jobNumber'>(
    'none'
  );
  const [filterValue, setFilterValue] = useState<string>('');
  const [debouncedFilterValue, setDebouncedFilterValue] = useState<string>('');

  useEffect(() => {
    if (filterBy === 'username') {
      setDebouncedFilterValue(filterValue.trim());
      return;
    }

    const t = window.setTimeout(() => {
      setDebouncedFilterValue(filterValue.trim());
    }, 350);
    return () => window.clearTimeout(t);
  }, [filterValue, filterBy]);

  const { data: employeesData = [], isLoading: employeesLoading } = useQuery(
    ['employees'],
    GET_EMPLOYEES_QUERY,
    {
      staleTime: 3 * 60 * 60 * 1000,
      enabled: isMenuMounted && filterBy === 'username',
      select: (response: any) => response?.data || []
    }
  );

  const employeeOptions = (employeesData as Employee[]).slice().sort((a, b) => {
    const aName = `${a.firstName} ${a.lastName}`;
    const bName = `${b.firstName} ${b.lastName}`;
    return aName.localeCompare(bName);
  });

  const {
    data: notificationsPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useInfiniteQuery(
    ['adminNotifications', activityType, filterBy, debouncedFilterValue],
    ({ pageParam = 1 }) =>
      getAdminNotifications({
        page: pageParam,
        limit: NOTIFICATIONS_PER_PAGE,
        ...(activityType !== 'all' ? { activityType } : {}),
        ...(filterBy === 'username' && debouncedFilterValue
          ? { username: debouncedFilterValue }
          : {}),
        ...(filterBy === 'jobNumber' && debouncedFilterValue
          ? { jobNumber: debouncedFilterValue }
          : {})
      }),
    {
      getNextPageParam: lastPage => {
        const response = lastPage.data as AdminActivityResponse;
        return response.page < Math.ceil(response.total / response.limit)
          ? response.page + 1
          : undefined;
      },
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      select: data => ({
        pages: data.pages.map(page => page.data),
        pageParams: data.pageParams
      }),
      enabled: isMenuMounted && Boolean(authUser)
    }
  );

  useEffect(() => {
    setIsMenuMounted(true);

    return () => setIsMenuMounted(false);
  }, []);

  const handleScrollFrame = useCallback(
    (values: {
      scrollTop: number;
      scrollHeight: number;
      clientHeight: number;
    }) => {
      const isAtBottom =
        values.scrollHeight - values.scrollTop <= values.clientHeight + 100;

      if (isAtBottom && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const activityItems =
    notificationsPages?.pages.flatMap(page => page.data) || [];

  return (
    <Dropdown.Menu
      align="end"
      className={classNames(
        className,
        'navbar-dropdown-caret py-0 notification-dropdown-menu shadow border border-300'
      )}
    >
      <Card className="position-relative border-0">
        <Card.Header className="p-2">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="text-black mb-0">All Activity</h5>
          </div>
          <div className="d-flex gap-2 mt-2">
            <select
              className="form-select form-select-sm"
              value={activityType}
              onChange={e =>
                setActivityType(
                  e.target.value as AdminActivityEntityType | 'all'
                )
              }
              style={{ maxWidth: 140 }}
            >
              <option value="all">All types</option>
              <option value="job">Jobs</option>
              <option value="task">Tasks</option>
            </select>
            <select
              className="form-select form-select-sm"
              value={filterBy}
              onChange={e => {
                const next = e.target.value as
                  | 'none'
                  | 'username'
                  | 'jobNumber';
                setFilterBy(next);
                setFilterValue('');
              }}
              style={{ maxWidth: 150 }}
            >
              <option value="none">No filter</option>
              <option value="username">User</option>
              <option value="jobNumber">Job #</option>
            </select>
            {filterBy === 'username' ? (
              <select
                className="form-select form-select-sm"
                value={filterValue}
                onChange={e => setFilterValue(e.target.value)}
                disabled={employeesLoading}
              >
                <option value="">
                  {employeesLoading ? 'Loading users…' : 'All users'}
                </option>
                {employeeOptions.map(emp => (
                  <option key={emp._id} value={emp.username}>
                    {emp.firstName} {emp.lastName}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="form-control form-control-sm"
                placeholder={
                  filterBy === 'jobNumber'
                    ? 'Filter by job number…'
                    : 'Select a filter…'
                }
                value={filterValue}
                onChange={e => setFilterValue(e.target.value)}
                disabled={filterBy === 'none'}
              />
            )}
            {filterBy === 'jobNumber' && filterValue && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setFilterValue('')}
              >
                Clear
              </button>
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-0" style={{ height: '27rem' }}>
          <Scrollbar onScrollFrame={handleScrollFrame}>
            {isLoading ? (
              <LoadingPlaceholder />
            ) : activityItems.length === 0 ? (
              <div className="p-3 text-center">No activity</div>
            ) : (
              activityItems.map((item, index) => (
                <ActivityLogItem
                  item={item}
                  className={classNames({
                    'border-bottom': index !== activityItems.length - 1
                  })}
                  key={item.id}
                />
              ))
            )}
            {isFetchingNextPage && (
              <div className="d-flex justify-content-center py-2">
                <Spinner animation="border" variant="primary" />
              </div>
            )}
          </Scrollbar>
        </Card.Body>
      </Card>
    </Dropdown.Menu>
  );
};

export default ActivityDropdownMenu;
